/**
 * Google Tag Manager — bootstrap e helper
 * ---------------------------------------------------------------
 * Substitui o gtag inline do index.html por um container GTM.
 * Todos os eventos (GA4, Meta Pixel, Google Ads) passam a ser
 * disparados pelo dataLayer e configurados no container GTM.
 */

export const GTM_ID = import.meta.env.VITE_GTM_ID || 'GTM-XXXXXXX'

/**
 * Inicializa o Google Tag Manager (snippet oficial).
 * Deve ser chamado uma única vez no bootstrap do app (main.jsx).
 */
export function initGTM() {
  if (typeof window === 'undefined') return
  if (window.dataLayer && window.dataLayer.find((i) => i['gtm.start'])) return

  ;(function (w, d, s, l, i) {
    w[l] = w[l] || []
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
    const f = d.getElementsByTagName(s)[0]
    const j = d.createElement(s)
    j.async = true
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i
    f.parentNode.insertBefore(j, f)
  })(window, document, 'script', 'dataLayer', GTM_ID)
}

/**
 * Push genérico para o dataLayer.
 * @param {object} payload - qualquer evento GA4-compatible
 */
export function pushEvent(payload) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

/**
 * Dispara um evento GA4 via dataLayer.
 * @param {string} name - nome do evento (ex: 'checklist_complete')
 * @param {object} params - parâmetros do evento
 */
export function trackEvent(name, params = {}) {
  pushEvent({ event: name, ...params })
}

/**
 * Dispara uma conversão (GA4 + Google Ads).
 * @param {string} eventName - ex: 'checklist_lead_submit'
 * @param {object} params - ex: { value: 0, currency: 'BRL', transaction_id: '...' }
 */
export function trackConversion(eventName, params = {}) {
  pushEvent({
    event: eventName,
    ...params,
  })
}

/**
 * Enhanced Conversions — PII hasheada (SHA-256) para Google Ads / Meta.
 * Só enviar após consentimento explícito (LGPD).
 *
 * @param {string} email
 * @param {string} phone - telefone E.164 (ex: 5585982286045)
 * @returns {Promise<{sha256_email: string, sha256_phone: string}>}
 */
export async function hashPII(email, phone) {
  const normalize = (s) => (s || '').trim().toLowerCase()
  const toE164 = (p) => {
    const d = (p || '').replace(/\D/g, '')
    return d.length <= 11 ? '55' + d : d
  }

  const enc = new TextEncoder()
  const digest = async (text) => {
    const buf = await crypto.subtle.digest('SHA-256', enc.encode(text))
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  return {
    sha256_email: await digest(normalize(email)),
    sha256_phone: await digest(toE164(phone)),
  }
}

/**
 * Lê cookie pelo nome (ex: _fbp, _fbc para dedup Meta).
 */
export function getCookie(name) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Consentimento LGPD — integrar com banner de cookies.
 */
export function hasConsent() {
  if (typeof window === 'undefined') return false
  return localStorage.getItem('cookie_consent') === 'accepted'
}
